import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { BACKEND_URL } from "../../../config/config";
import axios from "axios";
import SuccessResultPayment from "./resultPayment/SuccessResultPayment";
import FailureResultPayment from "./resultPayment/FailureResultPayment";
import PendingResultPayment from "./resultPayment/PendingResultPayment";

const POLLING_INTERVAL = 1000; // 1 seconde entre les calls
const MAX_POLLING_ATTEMPTS = 5; // 5 tentatives
const INITIAL_SPINNER_DELAY = 2000; // 2 secondes d'attente initiale avant polling

export default function PaymentResultPage() {
  const { declarationId } = useParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [declaration, setDeclaration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApiError, setIsApiError] = useState(false); // true pour erreurs d'API (404, 403, 500, etc.)
  const [statusMessage, setStatusMessage] = useState(""); // Message détaillé selon le statut
  const pollingAttempts = useRef(0);
  const pollingTimer = useRef(null);
  const initialSpinnerTimer = useRef(null);

  const fetchPaymentStatus = useCallback(async () => {
    try {
      // Nouvel endpoint: appel direct à l'API Worldline GetHostedCheckoutStatus
      const response = await axios.get(
        `${BACKEND_URL}/api/worldline/check-status/${declarationId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const data = response.data;

      // Récupérer la déclaration pour l'afficher dans les composants de résultat
      // (On pourrait aussi faire un appel séparé, mais on utilise apasStatus du backend)
      if (!declaration) {
        const declResponse = await axios.get(
          `${BACKEND_URL}/api/declaration/${declarationId}`,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );
        setDeclaration(declResponse.data);
      }

      // Logique de mapping selon les instructions :
      // 1. Si paymentStatus existe (createdPaymentOutput.payment.status) → l'utiliser
      // 2. Sinon → fallback sur hostedCheckoutStatus (status top-level)

      const { paymentStatus, hostedCheckoutStatus, apasStatus } = data;

      let currentStatus;
      let message;

      if (paymentStatus) {
        // Cas 1 : createdPaymentOutput.payment.status existe (statut précis)
        const result = mapWorldlinePaymentStatus(paymentStatus);
        currentStatus = result.status;
        message = result.message;
      } else if (hostedCheckoutStatus) {
        // Cas 2 : Fallback sur status top-level (session status)
        const result = mapHostedCheckoutStatus(hostedCheckoutStatus);
        currentStatus = result.status;
        message = result.message;
      } else {
        // Cas 3 : Pas de statut Worldline, utiliser apasStatus (fallback ultime)
        const result = mapApasStatus(apasStatus);
        currentStatus = result.status;
        message = result.message;
      }

      setPaymentStatus(currentStatus);
      setStatusMessage(message);

      // Si le statut n'est pas final, continuer le polling
      if (currentStatus === "pending" && pollingAttempts.current < MAX_POLLING_ATTEMPTS) {
        pollingAttempts.current += 1;
        pollingTimer.current = setTimeout(fetchPaymentStatus, POLLING_INTERVAL);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du statut:", error);

      let errorMessage;
      let isApiErrorFlag = false;
      const statusCode = error.response?.status;

      if (statusCode === 404) {
        // Obfusquer l'existence de l'ID pour raisons de sécurité
        errorMessage = "Droits insuffisants pour consulter cette déclaration";
        isApiErrorFlag = true; // Erreur d'API - pas de boutons
      } else if (statusCode === 500) {
        // Extraire le message du backend (peut contenir "Droits insuffisants...")
        errorMessage = error.response?.data?.message ||
                       error.response?.data?.error ||
                       "Erreur lors de la récupération du statut du paiement";
        isApiErrorFlag = true; // Erreur d'API - pas de boutons
      } else if (statusCode === 403 || statusCode === 401) {
        // Erreurs d'authentification/autorisation explicites
        errorMessage = error.response?.data?.message ||
                       "Droits insuffisants pour consulter cette déclaration";
        isApiErrorFlag = true; // Erreur d'API - pas de boutons
      } else {
        // Autres erreurs (réseau, timeout, etc.)
        errorMessage = error.response?.data?.message ||
                       error.response?.data?.error ||
                       "Erreur lors de la récupération du statut du paiement";
        isApiErrorFlag = true; // Erreur d'API - pas de boutons
      }

      setError(errorMessage);
      setIsApiError(isApiErrorFlag);
      setIsLoading(false);
    }
  }, [declarationId, declaration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!declarationId) {
      setError("ID de déclaration manquant");
      setIsLoading(false);
      return;
    }

    // Attendre 2 secondes avant de commencer le polling
    // Cela permet à Worldline de traiter la requête de paiement
    initialSpinnerTimer.current = setTimeout(() => {
      fetchPaymentStatus();
    }, INITIAL_SPINNER_DELAY);

    // Cleanup: arrêter les deux timers si le composant est démonté
    return () => {
      if (initialSpinnerTimer.current) {
        clearTimeout(initialSpinnerTimer.current);
      }
      if (pollingTimer.current) {
        clearTimeout(pollingTimer.current);
      }
    };
  }, [declarationId, fetchPaymentStatus]);

  /**
   * Mappe les statuts Worldline payment (createdPaymentOutput.payment.status)
   * Valeurs possibles: CAPTURED, PENDING_CAPTURE, REJECTED, AUTHORIZATION_REQUESTED, etc.
   */
  const mapWorldlinePaymentStatus = (paymentStatus) => {
    switch (paymentStatus) {
      case "CAPTURED":
        return { status: "success", message: "Payment successful" };
      case "PENDING_CAPTURE":
        return { status: "pending", message: "Payment confirmed, final processing..." };
      case "PENDING_APPROVAL":
        return { status: "pending", message: "Payment pending approval..." };
      case "AUTHORIZATION_REQUESTED":
        return { status: "pending", message: "Authorizing payment..." };
      case "REJECTED":
      case "CANCELLED":
      case "REJECTED_CAPTURE":
        return { status: "failure", message: "Payment failed" };
      // Autres statuts ongoing
      case "CREATED":
      case "REDIRECTED":
      case "PENDING_PAYMENT":
      default:
        return { status: "pending", message: "Processing payment..." };
    }
  };

  /**
   * Mappe les statuts de session Worldline (status top-level)
   * Valeurs possibles: PAYMENT_CREATED, IN_PROGRESS, CANCELLED_BY_CONSUMER
   */
  const mapHostedCheckoutStatus = (hostedCheckoutStatus) => {
    switch (hostedCheckoutStatus) {
      case "PAYMENT_CREATED":
        return { status: "pending", message: "We are confirming your payment..." };
      case "IN_PROGRESS":
        return { status: "pending", message: "Processing payment..." };
      case "CANCELLED_BY_CONSUMER":
        return { status: "failure", message: "You cancelled the payment" };
      default:
        return { status: "pending", message: "Processing payment..." };
    }
  };

  /**
   * Mappe les statuts APAS (fallback ultime)
   * Valeurs possibles: 'yes', 'no', 'ongoing'
   */
  const mapApasStatus = (apasStatus) => {
    switch (apasStatus) {
      case "yes":
        return { status: "success", message: "Payment successful" };
      case "no":
        return { status: "failure", message: "Payment failed" };
      case "ongoing":
      default:
        return { status: "pending", message: "Processing payment..." };
    }
  };

  const handleReturnToDeclarations = () => {
    navigate("/tableDeclaration");
  };

  const handleRetry = () => {
    // Rediriger vers la page de paiement
    navigate("/declaration-cb-page-v2", {
      state: { message: declaration },
    });
  };

  if (isLoading && !paymentStatus) {
    return (
      <div className="block-right">
        <div className="declaration-payment-page payment-loading-state">
          <div className="payment-loading-container">
            <h2>Vérification du paiement...</h2>
            <p className="payment-loading-message">
              Veuillez patienter pendant le traitement de votre paiement
            </p>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="block-right">
        <div className="declaration-payment-page">
          <FailureResultPayment
            message={error}
            onReturn={handleReturnToDeclarations}
            onRetry={handleRetry}
            showButtons={!isApiError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="block-right">
      <div className="declaration-payment-page payment-result-state">
        {paymentStatus === "success" && (
          <SuccessResultPayment
            declaration={declaration}
            onReturn={handleReturnToDeclarations}
          />
        )}
        {paymentStatus === "failure" && (
          <FailureResultPayment
            declaration={declaration}
            onReturn={handleReturnToDeclarations}
            onRetry={handleRetry}
          />
        )}
        {paymentStatus === "pending" && (
          <PendingResultPayment
            message={statusMessage}
            declaration={declaration}
            isPolling={pollingAttempts.current < MAX_POLLING_ATTEMPTS}
            onReturn={handleReturnToDeclarations}
          />
        )}
      </div>
    </div>
  );
}
